/**
 * Span helper functions for instrumenting critical flows
 *
 * Provides utilities for creating spans around operations to track
 * timing, errors, and metadata for distributed tracing.
 */

import * as api from '@opentelemetry/api';

import {telemetryConfig} from './config';
import {SpanAttributes} from './types';

/**
 * Get the OpenTelemetry tracer instance
 * Uses the configured service name for identification
 */
function getOtelTracer(): api.Tracer {
  return api.trace.getTracer(telemetryConfig.serviceName || 'liquor-store-frontend');
}

/**
 * Generic span wrapper for async operations
 *
 * Wraps an async function with span creation, automatic error handling,
 * and span lifecycle management.
 *
 * Usage:
 * ```typescript
 * const result = await withSpan('my-operation', async (span) => {
 *   span.setAttribute('user.id', userId);
 *   return await performOperation();
 * }, {
 *   'operation.type': 'user-action'
 * });
 * ```
 *
 * @param name - Span name (operation identifier)
 * @param fn - Async function to execute within span context
 * @param attributes - Optional initial span attributes
 * @returns Result from the wrapped function
 */
export async function withSpan<T>(
  name: string,
  fn: (span: api.Span) => Promise<T>,
  attributes?: SpanAttributes,
): Promise<T> {
  if (!telemetryConfig.enabled) {
    // Execute function without tracing if disabled
    // Pass a no-op span to maintain API compatibility
    const noopSpan = api.trace.wrapSpanContext(api.INVALID_SPAN_CONTEXT);
    return fn(noopSpan);
  }

  const otelTracer = getOtelTracer();
  const span = otelTracer.startSpan(name, {
    attributes: {
      'service.name': telemetryConfig.serviceName,
      ...attributes,
    },
  });

  try {
    const result = await fn(span);
    span.setStatus({code: api.SpanStatusCode.OK});
    return result;
  } catch (error) {
    // Record error in span
    span.setStatus({
      code: api.SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : 'Unknown error',
    });

    if (error instanceof Error) {
      span.recordException(error);
      span.setAttribute('error.message', error.message);
      if (error.stack) {
        span.setAttribute('error.stack', error.stack);
      }
    }

    // Re-throw error after recording
    throw error;
  } finally {
    span.end();
  }
}

/**
 * Synchronous span wrapper
 *
 * Similar to withSpan but for synchronous operations.
 *
 * Usage:
 * ```typescript
 * const result = withSpanSync('calculation', (span) => {
 *   span.setAttribute('input.count', data.length);
 *   return performCalculation(data);
 * });
 * ```
 */
export function withSpanSync<T>(
  name: string,
  fn: (span: api.Span) => T,
  attributes?: SpanAttributes,
): T {
  if (!telemetryConfig.enabled) {
    const noopSpan = api.trace.wrapSpanContext(api.INVALID_SPAN_CONTEXT);
    return fn(noopSpan);
  }

  const otelTracer = getOtelTracer();
  const span = otelTracer.startSpan(name, {
    attributes: {
      'service.name': telemetryConfig.serviceName,
      ...attributes,
    },
  });

  try {
    const result = fn(span);
    span.setStatus({code: api.SpanStatusCode.OK});
    return result;
  } catch (error) {
    span.setStatus({
      code: api.SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : 'Unknown error',
    });

    if (error instanceof Error) {
      span.recordException(error);
      span.setAttribute('error.message', error.message);
      if (error.stack) {
        span.setAttribute('error.stack', error.stack);
      }
    }

    throw error;
  } finally {
    span.end();
  }
}

/**
 * Pre-built span helpers for common critical flows
 */
export const SpanHelpers = {
  /**
   * Instrument checkout process
   *
   * Usage:
   * ```typescript
   * await SpanHelpers.checkout(cart.items.length, selectedShop.id, async (span) => {
   *   span.setAttribute('cart.total_amount', cart.totalAmount);
   *   return await submitOrder();
   * });
   * ```
   */
  checkout: async <T>(
    cartItemCount: number,
    shopId: string,
    fn: (span: api.Span) => Promise<T>,
  ): Promise<T> => {
    return withSpan('checkout_process', fn, {
      'cart.item_count': cartItemCount,
      'shop.id': shopId,
      'flow.type': 'checkout',
    });
  },

  /**
   * Instrument payment process
   *
   * Usage:
   * ```typescript
   * await SpanHelpers.payment(totalAmount, 'credit_card', async (span) => {
   *   return await processPayment();
   * });
   * ```
   */
  payment: async <T>(
    amount: number,
    method: string,
    fn: (span: api.Span) => Promise<T>,
  ): Promise<T> => {
    return withSpan('payment_process', fn, {
      'payment.amount': amount,
      'payment.method': method,
      'flow.type': 'payment',
    });
  },

  /**
   * Instrument authentication flows
   *
   * Usage:
   * ```typescript
   * await SpanHelpers.authentication('login', async (span) => {
   *   span.setAttribute('auth.provider', 'email');
   *   return await performLogin();
   * });
   * ```
   */
  authentication: async <T>(
    action: 'login' | 'logout' | 'signup' | 'verify',
    fn: (span: api.Span) => Promise<T>,
  ): Promise<T> => {
    return withSpan(`authentication_${action}`, fn, {
      'auth.action': action,
      'flow.type': 'authentication',
    });
  },

  /**
   * Instrument store selection
   *
   * Usage:
   * ```typescript
   * await SpanHelpers.storeSelection(shopId, async (span) => {
   *   return await selectStore();
   * });
   * ```
   */
  storeSelection: async <T>(shopId: string, fn: (span: api.Span) => Promise<T>): Promise<T> => {
    return withSpan('store_selection', fn, {
      'shop.id': shopId,
      'flow.type': 'store_selection',
    });
  },

  /**
   * Instrument product search
   *
   * Usage:
   * ```typescript
   * await SpanHelpers.productSearch(searchQuery, resultsCount, async (span) => {
   *   return await performSearch();
   * });
   * ```
   */
  productSearch: async <T>(
    query: string,
    resultsCount: number,
    fn: (span: api.Span) => Promise<T>,
  ): Promise<T> => {
    return withSpan('product_search', fn, {
      'search.query': query,
      'search.results_count': resultsCount,
      'flow.type': 'search',
    });
  },

  /**
   * Instrument navigation events
   *
   * Usage:
   * ```typescript
   * SpanHelpers.navigation('ProductDetailScreen', (span) => {
   *   span.setAttribute('product.id', productId);
   * });
   * ```
   */
  navigation: (screenName: string, fn?: (span: api.Span) => void): void => {
    if (!telemetryConfig.enabled) {
      return;
    }

    withSpanSync('navigation', span => {
      span.setAttribute('screen.name', screenName);
      span.setAttribute('flow.type', 'navigation');
      if (fn) {
        fn(span);
      }
    });
  },
};
